import * as webMercatorUtils from "@arcgis/core/geometry/support/webMercatorUtils.js";
import Polygon from "@arcgis/core/geometry/Polygon";
import Point from "@arcgis/core/geometry/Point";

export type SimpleFillStyle =
  | "solid"
  | "none"
  | "horizontal"
  | "vertical"
  | "forward-diagonal"
  | "backward-diagonal"
  | "cross"
  | "diagonal-cross";

export type MapItemPopup = {
  id: string;
  name: string;
  title: string;
  content: string;
  hasAction: boolean;
  actionType?: "edit" | "zoom";
};

export type MapItem = {
  geometry: __esri.Geometry;
  popup?: MapItemPopup;
  attributes?: any;
  color?: __esri.Color;
  style?: SimpleFillStyle;
};

const createMapItem = (
  type: "polygon" | "polyline",
  rings: number[][][],
  itemPopup?: MapItemPopup,
  itemAttributes?: any,
  color?: __esri.Color,
  style?: SimpleFillStyle
) => {
  return {
    geometry:
      type === "polygon"
        ? ({
            type,
            rings: rings,
            spatialReference: { wkid: 4326 },
          } as __esri.Polygon)
        : ({
            type,
            paths: rings,
            spatialReference: { wkid: 4326 },
          } as __esri.Polyline),
    popup: itemPopup,
    attributes: itemAttributes,
    color,
    style,
  } as MapItem;
};

const configAuth = (
  OAuthInfo: __esri.OAuthInfoConstructor,
  esriId: __esri.IdentityManager,
  authApiId: string,
  portalUrl: string,
  callbackUrl: string,
  token?: string
): Promise<void> => {
  const info = new OAuthInfo({
    appId: authApiId,
    flowType: "auto",
    popup: true,
    portalUrl: portalUrl,
    popupCallbackUrl: callbackUrl,
  });

  esriId.registerOAuthInfos([info]);

  if (token) {
    esriId.registerToken({
      token: token,
      server: info.portalUrl,
    });
  }
  return new Promise<void>((resolve, reject) => {
    esriId
      .checkSignInStatus(info.portalUrl + "/sharing")
      .then((x) => {
        console.log("user is  signed in!", x);
        resolve();
      })
      .catch((err) => {
        esriId.getCredential(info.portalUrl + "/sharing");
        reject(err);
      });
  });
};

const createWebMap = (
  WebMap: __esri.WebMapConstructor,
  GraphicsLayer: __esri.GraphicsLayerConstructor,
  portalItemId: string
) => {
  const webmap = new WebMap({
    portalItem: {
      id: portalItemId,
    },
  });

  const graphicsLayer = new GraphicsLayer({ title: "Permit Boundary" });

  webmap.when(() => {
    webmap.layers.add(graphicsLayer);
  });

  return { webmap, graphicsLayer };
};

const createMapView = (
  MapView: __esri.MapViewConstructor,
  ScaleBar: __esri.ScaleBarConstructor,
  Legend: __esri.LegendConstructor,
  container: any,
  webmap: __esri.WebMap,
  showLegend: boolean,
  targets?: () => __esri.Collection<__esri.Graphic>,
  center?: __esri.Point,
  onPopupClick?: (id: string) => void
) => {
  const view = new MapView({
    map: webmap,
    center,
    zoom: 9,
    container: container,
    constraints: {
      snapToZoom: false,
    },
    // spatialReference: { wkid: 4326 },
  });

  view.when(() => {
    if (targets) {
      view.goTo(targets());
    }

    if (onPopupClick) {
      view.popup.on("trigger-action", (event: any) => {
        onPopupClick(event.action.id);
      });
    }

    const scalebar = new ScaleBar({
      view: view,
    });
    view.ui.add(scalebar, "bottom-left");

    if (showLegend) {
      const legend = new Legend({
        view: view,
      });
      view.ui.add(legend, "top-right");
    }
  });

  return view;
};

const addSimpleMarker = (
  SimpleMarkerSymbol: __esri.SimpleMarkerSymbolConstructor,
  Graphic: __esri.GraphicConstructor,
  mapview: __esri.MapView,
  point: __esri.Point,
  color?: __esri.Color
) => {
  const simpleMarkerSymbol = new SimpleMarkerSymbol({
    color: color ? color : ({ r: 226, g: 119, b: 40 } as __esri.Color), // Orange
    outline: {
      color: { r: 255, g: 255, b: 255 } as __esri.Color, // White
      width: 1,
    },
  });

  // Add the geometry and symbol to a new graphic
  const graphic = new Graphic({
    geometry: point,
    symbol: simpleMarkerSymbol,
  });

  mapview.graphics.add(graphic);
};

const addPolyline = (
  SimpleLineSymbol: __esri.SimpleLineSymbolConstructor,
  Graphic: __esri.GraphicConstructor,
  PopupTemplate: __esri.PopupTemplateConstructor,
  graphicsLayer: __esri.GraphicsLayer,
  polyline: __esri.Polyline,
  color?: __esri.Color,
  popup?: MapItemPopup
) => {
  const lineSymbol = new SimpleLineSymbol({
    color: color ? ({ ...color } as __esri.Color) : ({ r: 226, g: 119, b: 40 } as __esri.Color),
    width: "4px",
    style: "short-dash",
  });

  return addGraphic(lineSymbol, Graphic, PopupTemplate, graphicsLayer, polyline, popup);
};

const addPolygon = (
  SimpleFillSymbol: __esri.SimpleFillSymbolConstructor,
  Graphic: __esri.GraphicConstructor,
  PopupTemplate: __esri.PopupTemplateConstructor,
  graphicsLayer: __esri.GraphicsLayer,
  polygon: __esri.Geometry,
  color?: __esri.Color,
  style?: SimpleFillStyle,
  popup?: MapItemPopup,
  attributes?: __esri.Graphic["attributes"]
) => {
  const fillSymbol = new SimpleFillSymbol({
    style: style ? style : "solid",
    color: color
      ? ({ ...color, a: style && style !== "solid" ? 0.5 : 0.15 } as __esri.Color)
      : ({ r: 226, g: 119, b: 40, a: style && style !== "solid" ? 0.5 : 0.15 } as __esri.Color),
    outline: {
      color: color ? ({ ...color } as __esri.Color) : ({ r: 226, g: 119, b: 40 } as __esri.Color),
      width: 1,
    },
  });

  return addGraphic(fillSymbol, Graphic, PopupTemplate, graphicsLayer, polygon, popup, attributes);
};

const addGraphic = (
  symbol: __esri.SimpleLineSymbol | __esri.SimpleFillSymbol,
  Graphic: __esri.GraphicConstructor,
  PopupTemplate: __esri.PopupTemplateConstructor,
  graphicsLayer: __esri.GraphicsLayer,
  geometry: __esri.Geometry,
  popup?: MapItemPopup,
  attributes?: any
) => {
  const graphic = new Graphic({
    geometry,
    symbol,
    // set attributes if provided
    ...(attributes && { attributes }),
  });

  if (popup) {
    const popupActions: any = popup.hasAction
      ? [
          {
            className: popup.actionType === "edit" ? "esri-icon-edit" : "esri-icon-zoom-in-magnifying-glass",
            title: popup.name,
            id: popup.id,
          },
        ]
      : [];

    const popupTemplate = new PopupTemplate({
      title: popup.title,
      content: popup.content,
      actions: popupActions,
      overwriteActions: true,
    });

    graphic.popupTemplate = popupTemplate;
  }

  graphicsLayer.add(graphic);

  return graphic;
};

const createSketchView = (
  SketchVMCtor: __esri.SketchViewModelConstructor,
  mapView: __esri.MapView,
  graphicsLayer: __esri.GraphicsLayer,
  FillSymbolCtor: __esri.SimpleFillSymbolConstructor,
  onDrawingCompleted: (graphic: __esri.Graphic) => void,
  onDrawinCancelled: () => void,
  onEditCompleted: (graphics: __esri.Graphic[]) => void
) => {
  mapView.ui.add("mainWidget", "top-right");

  const fillSymbol = new FillSymbolCtor({
    color: { r: 226, g: 119, b: 40, a: 0.15 } as __esri.Color, // { r: 0, g: 0, b: 0, a: 0.15 } as __esri.Color,
    outline: {
      color: { r: 226, g: 119, b: 40 } as __esri.Color, //{ r: 0, g: 0, b: 0 } as __esri.Color,
      width: 1,
    },
  });

  const sketchVM = new SketchVMCtor({
    view: mapView,
    layer: graphicsLayer,
    updateOnGraphicClick: false,
    polygonSymbol: fillSymbol,

    defaultUpdateOptions: {
      toggleToolOnClick: false, // only reshape operation will be enabled
    },
  });

  sketchVM.on("create", (event) => {
    if (event.state === "complete") {
      onDrawingCompleted(event.graphic);
    } else if (event.state === "cancel") {
      onDrawinCancelled();
    }
  });

  sketchVM.on("update", (event) => {
    if (event.state === "complete") {
      // This event is called when either the "transform" or "reshape" is cancelled/finished
      onEditCompleted(event.graphics);
    }
  });

  return sketchVM;
};

const ensureGeographic = (polygon: __esri.Polygon): __esri.Polygon => {
  // If it’s already geographic, do nothing:
  if (polygon.spatialReference.isGeographic) {
    return polygon;
  }

  // Otherwise, convert each point back into lon/lat:
  const rings4326 = polygon.rings.map((ring) =>
    ring.map(([x, y]) => {
      // Create a Point WITHOUT the 'type' property
      const mercPt = new Point({
        x,
        y,
        spatialReference: polygon.spatialReference,
      });

      // Reproject and cast to __esri.Point so TS knows about x/y
      const geoPt = webMercatorUtils.webMercatorToGeographic(mercPt) as __esri.Point;

      return [geoPt.x, geoPt.y] as [number, number];
    })
  );

  // Return a true __esri.Polygon instance
  return new Polygon({
    rings: rings4326,
    spatialReference: { wkid: 4326 },
  });
};

export {
  createMapItem,
  configAuth,
  createMapView,
  createWebMap,
  createSketchView,
  addPolygon,
  addPolyline,
  addSimpleMarker,
  ensureGeographic,
};
